import CustomerContactDisplay from "@/modules/common/components/customer-contact";
import { resolveCustomerContact } from "@/modules/common/utils/customer-contact";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { useContext } from "react";
import { ContentCopy } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import ActionsCompact from "./actions-compact";
import InCallIndicator from "@/modules/common/components/ui-kit/in-call-indicator";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";

interface PhoneFieldProps {
  phoneNumber?: string;
  doNotCall?: boolean;
  isOnCall?: boolean;
  agentName?: string;
}

const PhoneField = ({
  phoneNumber,
  doNotCall,
  isOnCall,
  agentName,
}: PhoneFieldProps) => {
  const theme = useTheme();
  const { lead } = useContext(LeadPageContext);
  const contact = resolveCustomerContact(
    lead?.data?.lead?.lead_telephones ??
      (phoneNumber ? [{ telephone: phoneNumber }] : []),
    doNotCall || lead?.data?.lead?.do_not_call,
  );
  if (contact.kind === "hidden") return <CustomerContactDisplay hidden />;
  const usernameOnly = contact.kind === "whatsapp";
  const copyLabel = usernameOnly ? "Copy username" : "Copy phone number";
  const copyContact = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(contact.username ?? contact.label);
      toast.success(usernameOnly ? "Username copied" : "Phone number copied", {
        position: "bottom-left",
        autoClose: 2000,
        toastId: "customer-contact-copy",
      });
    } catch {
      toast.error("Could not copy. Please select and copy the contact.", {
        position: "bottom-left",
        toastId: "customer-contact-copy-error",
      });
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box",
        minWidth: 0,
        gap: theme.nawy.spacing.sm,
        border: `1px solid ${theme.nawy.colors.primitives.navy[100]}`,
        padding: `${theme.nawy.spacing.sm} ${theme.nawy.spacing.md}`,
        borderRadius: theme.nawy.spacing.md,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          minWidth: 0,
          gap: theme.nawy.spacing.xs,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            sx={{
              color: theme.nawy.colors.primitives.grey[700],
              ...theme.nawy.typography.english.variants.body.sm.regular,
            }}
          >
            {usernameOnly ? "WhatsApp username" : "Primary number"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              gap: theme.nawy.spacing.xxs,
            }}
          >
            <Typography
              dir="ltr"
              title={contact.phone ?? contact.label}
              sx={{
                color: theme.nawy.colors.semantic.text.accent,
                ...theme.nawy.typography.english.variants.body.lg.regular,
                minWidth: 0,
                overflowWrap: "anywhere",
                userSelect: "text",
              }}
            >
              {contact.phone ?? contact.label}
            </Typography>
            {(usernameOnly || contact.kind === "unverified-phone") && (
              <Tooltip title={copyLabel}>
                <IconButton
                  size="small"
                  aria-label={copyLabel}
                  onClick={copyContact}
                  sx={{ flexShrink: 0 }}
                >
                  <ContentCopy sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.CAN_VIEW_IN_CALL_TAG,
                level: PermissionLevel.READ,
              },
            ]}
          >
            {isOnCall && (
              <InCallIndicator agentName={agentName} variant="secondary" />
            )}
          </PermissionGuard>
          <ActionsCompact
            phoneNumber={contact.phone}
            whatsappUsername={contact.username}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PhoneField;
